/**
 * CaribNavi Mansion profit stack — mirrors DHC lib/navi/navi-profit-model.ts
 */

export const ISLAND_TAX_RATES = {
  barbados: 0.175,
  jamaica: 0.15,
  trinidad: 0.125,
  bahamas: 0.1,
};

export const NAVI_PROFIT_DEFAULTS = {
  dhcPlatformFeePercent: 0.15,
  bookingServiceFeeUsd: 4.99,
  paymentProcessingPercent: 0.032,
  paymentProcessingFixedUsd: 0.35,
  explorerServiceFeePercent: 0.05,
  proExplorerDiscountPercent: 0.1,
  partnerTiers: {
    standard: { platformFeePercent: 0.15, monthlySubscriptionUsd: 0 },
    featured: { platformFeePercent: 0.12, monthlySubscriptionUsd: 29.99 },
    pro: { platformFeePercent: 0.1, monthlySubscriptionUsd: 79.99 },
  },
  subscriptions: {
    explorerProMonthlyUsd: 9.99,
    explorerProYearlyUsd: 79.99,
    explorerProLifetimeUsd: 199.99,
    businessFeaturedMonthlyUsd: 29.99,
    businessProMonthlyUsd: 79.99,
  },
  adRatesDailyUsd: {
    sponsored: 10,
    featured: 25,
    premium: 75,
    homepage_banner: 150,
    category_top: 50,
  },
};

export function getIslandTaxRate(country = "barbados") {
  return ISLAND_TAX_RATES[String(country || "barbados").toLowerCase()] ?? 0.175;
}

export function getPartnerTierConfig(tier = "standard") {
  return NAVI_PROFIT_DEFAULTS.partnerTiers[tier] ?? NAVI_PROFIT_DEFAULTS.partnerTiers.standard;
}

function roundUsd(value) {
  return Math.round(value * 100) / 100;
}

export function calculateNaviPartnerPricing({
  desiredPayout = 100,
  country = "barbados",
  partnerTier = "standard",
} = {}) {
  const payout = Math.max(Number(desiredPayout) || 0, 0);
  const taxRate = getIslandTaxRate(country);
  const tierCfg = getPartnerTierConfig(partnerTier);
  const platformFeePercent = tierCfg.platformFeePercent;

  const dhcPlatformFee = roundUsd(payout * platformFeePercent);
  const bookingServiceFee = roundUsd(NAVI_PROFIT_DEFAULTS.bookingServiceFeeUsd);
  const taxableSubtotal = roundUsd(payout + dhcPlatformFee + bookingServiceFee);
  const paymentProcessingRecovery = roundUsd(
    taxableSubtotal * NAVI_PROFIT_DEFAULTS.paymentProcessingPercent +
      NAVI_PROFIT_DEFAULTS.paymentProcessingFixedUsd
  );
  const vatBase = roundUsd(taxableSubtotal + paymentProcessingRecovery);
  const vatCollected = roundUsd(vatBase * taxRate);
  const totalPrice = roundUsd(vatBase + vatCollected);

  const dhcGrossRevenue = roundUsd(dhcPlatformFee + bookingServiceFee + paymentProcessingRecovery);
  const estimatedPaymentProcessorCost = roundUsd(totalPrice * 0.029 + 0.3);
  const dhcNetAfterProcessing = roundUsd(Math.max(dhcGrossRevenue - estimatedPaymentProcessorCost, 0));

  return {
    desiredPayout: payout,
    country: String(country || "barbados").toLowerCase(),
    taxRate,
    partnerTier,
    platformFeePercent,
    dhcPlatformFee,
    bookingServiceFee,
    paymentProcessingRecovery,
    vatCollected,
    taxableSubtotal: vatBase,
    totalPrice,
    businessPayout: payout,
    dhcGrossRevenue,
    estimatedPaymentProcessorCost,
    dhcNetAfterProcessing,
    vatRemitted: vatCollected,
    dhcFee: dhcPlatformFee,
    dhcFeeRate: platformFeePercent,
  };
}

export function calculateReverseMeetupPricing(options) {
  return calculateNaviPartnerPricing(options);
}

export const DHC_HQ_PRICING_URL = "https://digitalhustlerscaribbean.com/api/navi/pricing";

export async function fetchHqNaviPricing({
  desiredPayout = 100,
  country = "barbados",
  partnerTier = "standard",
} = {}) {
  const params = new URLSearchParams({
    desiredPayout: String(desiredPayout),
    country,
    partnerTier,
  });
  const response = await fetch(`${DHC_HQ_PRICING_URL}?${params}`, {
    cache: "no-store",
    headers: { "X-DHC-Hub": "navi" },
  });
  if (!response.ok) {
    throw new Error("HQ pricing unavailable");
  }
  const body = await response.json();
  return body.pricing;
}

export function calculateNaviExplorerBookingPricing({
  basePrice = 0,
  participants = 1,
  country = "barbados",
  partnerTier = "standard",
  userTier = "basic",
} = {}) {
  const subtotal = Math.max(Number(basePrice) || 0, 0) * Math.max(Number(participants) || 1, 1);
  const partner = calculateNaviPartnerPricing({ desiredPayout: subtotal, country, partnerTier });
  const explorerFee = roundUsd(subtotal * NAVI_PROFIT_DEFAULTS.explorerServiceFeePercent);
  const proDiscount =
    userTier === "pro"
      ? roundUsd(explorerFee * NAVI_PROFIT_DEFAULTS.proExplorerDiscountPercent)
      : 0;
  return {
    ...partner,
    subtotal,
    explorerServiceFee: explorerFee,
    proExplorerDiscount: proDiscount,
    totalPrice: roundUsd(partner.totalPrice + explorerFee - proDiscount),
    dhcTotalRevenue: roundUsd(partner.dhcGrossRevenue + explorerFee - proDiscount),
  };
}

export function listNaviProfitStreams() {
  const d = NAVI_PROFIT_DEFAULTS;
  return [
    {
      id: "platform_fee",
      title: "Mansion platform fee",
      description: `${(d.dhcPlatformFeePercent * 100).toFixed(0)}% of partner payout (lower % for paying business tiers).`,
    },
    {
      id: "booking_service",
      title: "Booking operations fee",
      description: `US$${d.bookingServiceFeeUsd.toFixed(2)} per checkout.`,
    },
    {
      id: "processing_recovery",
      title: "Payment processing recovery",
      description: "Covers card processing so margin stays sound.",
    },
    {
      id: "explorer_fee",
      title: "Explorer service fee",
      description: `${(d.explorerServiceFeePercent * 100).toFixed(0)}% on guest experience bookings.`,
    },
    {
      id: "subscriptions",
      title: "PRO subscriptions",
      description: `Explorer & business plans from US$${d.subscriptions.explorerProMonthlyUsd}/mo.`,
    },
    {
      id: "ads",
      title: "Sponsored listings",
      description: "Daily ad rates for featured discovery placement.",
    },
  ];
}
